import { useMemo, useState } from "react";
import {
  useGetInvoicesQuery,
  useGetCustomersQuery,
  useDeleteInvoiceMutation,
} from "../../../../redux/api";
import type { Invoice } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { InvoiceFormModal } from "./invoice";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 7;

const STATUS_TONE: Record<string, string> = {
  draft: "badge--info",
  sent: "badge--warning",
  partial: "badge--warning",
  paid: "badge--success",
  overdue: "badge--danger",
  cancelled: "badge--danger",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Inventory → Invoices (list page).
const Invoices = () => {
  const {
    data: invoices = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetInvoicesQuery();

  const { data: customers = [] } = useGetCustomersQuery();

  const [deleteInvoice] = useDeleteInvoiceMutation();

  const [search, setSearch] = useState("");

  const customerName = (id?: string | null) =>
    id ? customers.find((c) => c.id === id)?.name ?? id : "—";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = invoices.find((i) => i.id === viewingId) ?? null;


  const handleDelete = async (invoice: Invoice) => {
    if (!window.confirm(`Delete invoice "${invoice.invoiceNumber}"?`)) return;
    try {
      await deleteInvoice(invoice.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete invoice.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((i: Invoice) =>
      [i.invoiceNumber, i.status, customerName(i.customerId)].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, search, customers]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load invoices"
    : null;

  if (viewing) {
    const balance = viewing.total - (viewing.amountPaid ?? 0);
    return (
      <>
        <EntityOverview
          backLabel="Invoices"
          onBack={() => setViewingId(null)}
          title={viewing.invoiceNumber ?? viewing.id}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Customer", value: customerName(viewing.customerId) },
              { label: "Invoice date", value: formatDate(viewing.invoiceDate) },
              { label: "Due date", value: formatDate(viewing.dueDate) },
              { label: "Tax %", value: viewing.taxPercentage ?? 0 },
              { label: "Notes", value: viewing.notes, full: true },
            ]}
          />

          <h3 className="panel__section-title">Line items</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>Product</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>Line total</th>
                </tr>
              </thead>
              <tbody>
                {viewing.items.length === 0 ? (
                  <tr>
                    <td className="data-table__empty" colSpan={4}>
                      No items.
                    </td>
                  </tr>
                ) : (
                  viewing.items.map((row, index) => (
                    <tr key={index}>
                      <td>{row.productName || row.productId}</td>
                      <td>{row.quantity}</td>
                      <td>{row.unitPrice.toLocaleString()}</td>
                      <td>{row.lineTotal.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="totals">
            <div>
              <span>Subtotal</span>
              <strong>{viewing.subtotal.toLocaleString()}</strong>
            </div>
            <div>
              <span>Tax ({viewing.taxPercentage ?? 0}%)</span>
              <strong>{viewing.taxAmount.toLocaleString()}</strong>
            </div>
            <div className="totals__grand">
              <span>Total</span>
              <strong>{viewing.total.toLocaleString()}</strong>
            </div>
            <div>
              <span>Amount paid</span>
              <strong>{(viewing.amountPaid ?? 0).toLocaleString()}</strong>
            </div>
            <div className="totals__grand">
              <span>Balance due</span>
              <strong>{balance.toLocaleString()}</strong>
            </div>
          </div>
        </EntityOverview>
        <InvoiceFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Invoices</h2>
            <p className="panel__sub">
              Showing {visible.length} of {invoices.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search invoices…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New invoice
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Due</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading invoices…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {invoices.length === 0
                      ? "No invoices yet."
                      : "No invoices match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((invoice: Invoice) => (
                  <tr key={invoice.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(invoice.id)}
                      >
                        {invoice.invoiceNumber}
                      </button>
                    </td>
                    <td>{customerName(invoice.customerId)}</td>
                    <td className="cell-date">{formatDate(invoice.invoiceDate)}</td>
                    <td className="cell-date">{formatDate(invoice.dueDate)}</td>
                    <td className="cell-date">
                      {invoice.total?.toLocaleString?.() ?? invoice.total}
                    </td>
                    <td>
                      <span className={`badge ${statusTone(invoice.status)}`}>
                        <span className="badge__dot" />
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(invoice.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(invoice.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(invoice),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <InvoiceFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Invoices;
